<template>
  <div class="gp-main-body-content gf_template-list__container">
    <div class="row mb-4 gf_template-list__breadcrumb" :class="{ 'gf_template-list__breadcrumb--fixed': isFixedNav }">
      <div class="col-6">
        <div class="d-flex aling-items-center">
          <button class="btn btn-light3 action-back mr-half" @click="goBack">
            <i class="icon icon-arrow-left typo-semibold"></i>
          </button>
          <h2 class="typo-heading24 typo-semibold text-light-high align-items-center d-flex flex-grow-1">
            <template v-if="isNotSearching">
              {{ $tc("generals.template", 2) }}
            </template>
            <template v-else>
              {{ $t("dashboard.templates.result") }}
            </template>
          </h2>
        </div>
      </div>
      <div class="col-6">
        <GpInputSearch v-model="query" :placeholder="$t('dashboard.templates.placeholder')" clear />
      </div>
    </div>
    <div :class="{ 'gf_template-list-nav--fixed': isFixedNav }">
      <div class="gf_template-list-nav" :class="classFixedNav">
        <div class="col-10">
          <div class="row mx-0">
            <GpTabs class="flex-grow-0" @tabChange="onTabChangeHandler" :customNav="true" v-if="isNotSearching" :active.sync="filter">
              <template #GpTabsNav="{activeIndex, handler}">
                <GpTabsNav class="tag-filter-nav">
                  <GpTab class="tag-filter" :class="{ 'ml-1 ml-md-3': ind != 0 }" :index="t.key" :activeIndex="activeIndex || 'landingpages'" @click="handler" v-for="(t, ind) in tagFilters" :key="'tab_' + t.key">
                    <p class="typo-button14 typo-medium nowrap">{{ t.text }}</p>
                  </GpTab>
                </GpTabsNav>
              </template>
            </GpTabs>
          </div>
        </div>
        <div class="col-2 d-flex justify-content-end">
          <FilterSelect bpadding="pl-1 pr-1 mr-n1" v-model="sort" />
        </div>
      </div>
    </div>
    <div class="row">
      <!-- In case of filtering (not searching) -->
      <template v-if="isNotSearching">
        <AddPage class="pb-1 pt-1 mt-n1" @onClickAddPage="onCreateNewPage(0)" />
        <template v-if="!fetched">
          <PageTemplateItem v-for="i in 19" :key="'ap_' + i" :shopIsPro="shopIsPro" />
        </template>
        <template v-if="fetched">
          <PageTemplateItem :item="template" v-for="(template, i) in filteredTemplates" :shopIsPro="shopIsPro" :primaryAction="onCreateNewPage" :secondaryAction="previewTemplate" :key="i" />
        </template>
      </template>
      <!-- When searching -->
      <template v-else>
        <!-- If there're at least one templates match -->
        <template v-if="filteredTemplates && filteredTemplates.length">
          <PageTemplateItem :item="template" v-for="(template, i) in filteredTemplates" :shopIsPro="shopIsPro" :primaryAction="onCreateNewPage" :secondaryAction="previewTemplate" :key="i" />
        </template>
        <!-- Otherwise show message and give recommend -->
        <template v-else>
          <div class="col-12">
            <div class="row justify-content-center">
              <div class="col-4">
                <div class="empty-results">
                  <h4 class="typo-heading24 typo-semibold text-light-high mb-1">{{ $t("search.noResult", { keyword: query }) }}</h4>
                  <p class="typo-paragraph14 typo-regular text-light-low">{{ $t("search.tryAnother") }}</p>
                  <p class="typo-paragraph14 typo-regular text-light-low">{{ $t("dashboard.templates.tryLastest") }}</p>
                </div>
              </div>
            </div>
          </div>
          <PageTemplateItem :item="template" v-for="(template, i) in recommended" :shopIsPro="shopIsPro" :primaryAction="onCreateNewPage" :secondaryAction="previewTemplate" :key="i" />
        </template>
      </template>
    </div>

    <!-- Modal Preview Template -->
    <PreviewTemplate v-if="showPreview" :template="template_id" @close="closePreview" @select="onCreateNewPage" :list="templates" :hideEdit="accountExpired" />

    <!-- Modal Create Page -->
    <ModalCreatePage v-if="showModal" :shop="shopInfo.shopifyDomain" :template.sync="template_id" :type.sync="pageType" @close="closeModal" :hidePrimary="accountExpired" />
  </div>
</template>

<script>
import { PreviewTemplate, PageTemplateItem, ModalCreatePage } from "../../Shopify/components";
import { GpInputSearch } from "@/core/shared/GpForms";
import AddPage from "@/core/shared/GpAddPage";
import { mapGetters, mapActions, mapState } from "vuex";
import GpTabs, { GpTab, GpTabsNav } from "@/core/shared/GpTabs";
import { FilterSelect } from "../components";
import { TEMPLATE_LIST_EVENT, DESIGNED_TEMPLATE_PREVIEW_EVENT } from "@/config/constants/trackEventIds";

export default {
  name: "TemplateList",
  components: {
    PageTemplateItem,
    AddPage,
    PreviewTemplate,
    ModalCreatePage,
    GpInputSearch,
    GpTabs,
    GpTabsNav,
    GpTab,
    FilterSelect
  },
  props: {
    item: {
      type: Object,
      default: () => {
        return {};
      }
    },
    type: String
  },
  data() {
    return {
      showPreview: false,
      showModal: false,
      template_id: "0",
      fetched: false,
      previousRoute: null,

      query: "",
      sort: "newest",
      filter: "subpages",
      isFixedNav: false
    };
  },
  computed: {
    ...mapGetters("page", {
      allTemplates: "allTemplates",
      getTemplate: "getTemplate"
    }),
    ...mapGetters("account", {
      shopIsPro: "shopIsPro"
    }),
    ...mapState("account", {
      shopInfo: "shopInfo",
      permissions: "permissions"
    }),
    ...mapState("page", {
      pagesList: "pages"
    }),
    pageType() {
      if (this.type) return this.type;
      let filter = this.filter;
      if (filter == "blog") {
        filter = "articlepages";
      }
      if (!this.filter) {
        return "subpages";
      }
      return filter;
    },
    accountExpired() {
      return this.shopInfo.plan == "trial" && this.shopInfo.remainingDays <= 0;
    },
    isNotSearching() {
      return !this.query || !this.query.length;
    },
    selectedTemplate() {
      let output = this.getTemplate(this.template_id);
      return output;
    },
    templates() {
      let arr = JSON.parse(JSON.stringify(this.allTemplates));
      arr.sort((a, b) => {
        if (a.status == "new" && b.status != "new") return -1;
        // filter tag
        if (this.filter == "subpages") {
          let aHasLandingPage = a.tags.indexOf("subpages") > -1;
          let bHasLandingPage = b.tags.indexOf("subpages") > -1;
          let aHasSubPage = a.tags.indexOf("subpages") > -1;
          let bHasSubPage = b.tags.indexOf("subpages") > -1;

          let count = 0;
          if (aHasLandingPage) count -= 10;
          if (bHasLandingPage) count += 10;
          if (aHasSubPage) count -= 5;
          if (bHasSubPage) count += 5;
          if (count != 0) return count;
        } else {
          if (a.tags.indexOf(this.filter) > -1 && b.tags.indexOf(this.filter) < 0) return -1;
        }
        if (a.status == "soon" && b.status != "soon") return 1;
        switch (this.sort) {
          case "newest": {
            return a.created_at - b.created_at;
          }
          case "view": {
            return b.view - a.view;
          }
          default: {
            if (a.name > b.name) {
              return 1;
            }
            if (a.name < b.name) {
              return -1;
            }
            return 0;
          }
        }
      });
      return arr;
    },
    tagFilters() {
      return [
        {
          text: this.$tc("page.dashboardLandingPages", 2),
          key: "landingpages"
        },
        // {
        //   text: this.$tc("page.subpages", 2),
        //   key: "subpages"
        // },
        {
          text: this.$tc("page.homepages", 2),
          key: "homepages"
        },
        {
          text: this.$tc("page.productpages", 2),
          key: "productpages"
        },
        {
          text: this.$tc("page.collectionpages", 2),
          key: "collectionpages"
        },
        {
          text: this.$t("page.blog"),
          key: "blog"
        }
      ];
    },
    filteredTemplates() {
      let arr = [...this.templates];
      let arrange = item => {
        let iName = (item && item.name && item.name.toLowerCase()) || "";
        let query = (this.query && this.query.toLowerCase()) || "";
        return iName.indexOf(query) > -1;
      };
      if (this.isNotSearching) {
        arrange = item => {
          let iTag = (item && item.tags) || "";
          if (!iTag || iTag == "") return true;
          if (["subpages", "landingpages"].includes(this.filter)) {
            if (iTag.indexOf("subpages") > -1 || iTag.indexOf("landingpages") > -1) return true;
          }
          if (this.filter == "blog") {
            if (iTag.indexOf("articlepages") > -1) return true;
          }
          return iTag.indexOf(this.filter) > -1;
        };
      }
      return arr.filter(arrange);
    },
    recommended() {
      return this.templates.filter((item, ind) => ind < 4);
    },
    classFixedNav() {
      return this.isFixedNav ? "container px-1 px-xs-2 px-sm-4 px-md-5 px-xl-6 px-lg-2 px-mlg-2" : "";
    }
  },
  watch: {
    "$route.hash"(newV, oldV) {
      if (newV == oldV) return;
      let x = newV;
      x = x.replace("#", "");
      this.filter = x;
    }
  },
  created() {
    this.fetch();
    let x = this.$route.hash;
    if (x && x.length) {
      this.filter = x.replace("#", "");
    } else {
      this.filter = "";
    }
  },
  mounted() {
    this.handleScroll();
  },
  methods: {
    ...mapActions("page", {
      fetchTemplates: "fetchTemplates",
      createPageWithOneTap: "createPageWithOneTap"
    }),
    ...mapActions("common", {
      trackEvent: "trackEvent"
    }),
    onTabChangeHandler(tab) {
      this.filter = tab;
      this.$router.replace({ name: this.$route.name, hash: "#" + tab });
      this.trackViewedEvent();
    },
    fetch() {
      this.fetchTemplates({
        shop: this.shopInfo.shopifyDomain,
        tags: this.type,
        onSuccess: () => {
          this.fetched = true;
          this.trackViewedEvent();
        }
      });
    },
    enterName(id) {
      this.template_id = id;
      this.showModal = true;
      this.autoFocus = true;
      this.trackTemplateActionEvent(TEMPLATE_LIST_EVENT.START_EDITING_CLICKED);
    },
    previewTemplate(id) {
      this.template_id = id;
      this.showPreview = true;
      this.trackTemplateActionEvent(TEMPLATE_LIST_EVENT.PREVIEW_CLICKED);
    },
    closePreview() {
      this.showPreview = false;
      this.trackTemplateActionEvent(DESIGNED_TEMPLATE_PREVIEW_EVENT.CLOSE_CLICKED);
    },
    selectTemplate(id) {
      this.template_id = id;
      this.openModal();
      this.trackTemplateActionEvent(DESIGNED_TEMPLATE_PREVIEW_EVENT.START_EDITING_CLICKED);
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    openBlankModal() {
      this.trackCreatePageEvent();
      this.openModal();
      this.template_id = "0";
    },
    goBack() {
      if (this.previousRoute) {
        this.$router.go(-1);
        return;
      }
      this.$router.replace({ name: "Dashboard" });
    },
    trackViewedEvent() {
      const type = this.filter || "all";
      const designedTemplates = this.filteredTemplates.map((template, index) => {
        return { id: template.id, name: template.name, tags: template.tags || "All", position: index + 1 };
      });

      this.trackEvent({
        event_id: TEMPLATE_LIST_EVENT.VIEWED,
        properties: {
          list_type: this.templateType(type),
          designed_templates: designedTemplates
        }
      });
    },
    trackCreatePageEvent() {
      const type = this.filter || "subpages";

      this.trackEvent({
        event_id: TEMPLATE_LIST_EVENT.CREATE_NEW_PAGE_CLICKED,
        properties: {
          template_type: this.templateType(type)
        }
      });
    },
    trackTemplateActionEvent(eventId) {
      const type = this.filter || "subpages";
      const template = this.filteredTemplates.find(template => template.id == this.template_id);

      this.trackEvent({
        event_id: eventId,
        properties: {
          template_type: this.templateType(type),
          designed_template: {
            id: template.id,
            name: template.name,
            tags: template.tags || "All",
            position: this.filteredTemplates.indexOf(template) + 1
          }
        }
      });
    },
    templateType(type) {
      switch (type) {
        case "subpages":
          return "landingpages";
        case "articlepages":
          return "blogposts";
        default:
          return type;
      }
    },
    handleScroll() {
      const body = document.getElementById("mainBody");
      if (body) {
        body.onscroll = () => {
          const scrollY = body.scrollTop;
          if (scrollY > "128") {
            this.$nextTick(() => {
              this.isFixedNav = true;
            });
          } else {
            this.$nextTick(() => {
              this.isFixedNav = false;
            });
          }
        };
      }
    },
    onCreateNewPage(templateId) {
      const type = this.pageType || "subpages";
      this.createPageWithOneTap({ type: type, templateId: templateId });
      return false;
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousRoute = from;
      // if (!vm.permissions[vm.type]) {
      if (vm.accountExpired) {
        if (from.name != "Pricing") vm.$router.replace({ name: "Pricing" });
        else vm.$router.replace({ name: "Dashboard" });
      }
      return true;
    });
  },
  getUserIndustryServer() {
    const surveys = this.shopInfo && this.shopInfo.surveys;
    if (!surveys) return;
    const newSurveyObs = surveys.filter(sv => sv.handle == "onboarding-v2") || [];
    if (newSurveyObs && newSurveyObs.length > 0) {
      newSurveyObs.industries.forEach(item =>
        this.userIndustries.unshift(
          item
            .toLowerCase()
            .replace(" & ", "-")
            .replace(", ", "-")
            .replace(" ", "-")
        )
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.nowrap {
  white-space: nowrap;
}
.action-back {
  font-size: 16px;
  line-height: 16px;
  padding: 12px;
  color: $textLightModeLow;
  &:hover {
    color: $textLightModeHigh;
  }
}
.tag-filter {
  border: none;
  // &:not(:first-child) {
  //   margin-left: 32px;
  // }
}
.empty-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  // min-height: 222px;
  padding: 52px 0 84px 0;
}
::v-deep {
  .page-block {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border-radius: $radius;
    flex: 1;
    // margin-left: 24px;
    margin-top: 24px;
    padding: 16px;
    // 228x318
    // min-width: calc(228px);
    // min-height: calc(318px);
    // width: calc(25% - 24px);
    // max-width: calc(228px);
    // max-height: calc(318px);
  }
}
.gf_template-list {
  &__container {
    position: relative;
  }
  &__breadcrumb--fixed {
    margin-bottom: 78px !important;
  }
  &-nav {
    top: 0;
    display: flex;
    flex-wrap: wrap;
    margin-left: -12px;
    margin-right: -12px;
    ::v-deep .gp-tabs-nav-item {
      height: 46px;
      align-items: center;
    }
    &--fixed {
      position: fixed;
      top: 60px;
      width: calc(100% - 280px);
      left: 280px;
      margin-left: 0;
      margin-right: 0;
      z-index: 5;
      background: #eeeeee;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.06), 0px 2px 8px rgba(0, 0, 0, 0.12);
      .col-10 {
        padding-left: 0;
      }
      .col-2 {
        padding-right: 0;
      }
      @media screen and (max-width: 1279px) {
        width: calc(100% - 224px);
        left: 224px;
      }
    }
  }
}
</style>
